import { Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, getToken } from "./lib/api";
import SignIn from "./pages/SignIn";
import Shell from "./components/Shell";
import Dashboard from "./pages/Dashboard";
import RegionList from "./pages/RegionList";
import RegionDetail from "./pages/RegionDetail";
import ProducerList from "./pages/ProducerList";
import ProducerDetail from "./pages/ProducerDetail";
import BottleList from "./pages/BottleList";
import BottleDetail from "./pages/BottleDetail";
import TastingNoteList from "./pages/TastingNoteList";
import TastingNoteDetail from "./pages/TastingNoteDetail";

/** Gates the app on a session that the SERVER agrees is valid.
 *
 *  Checking only for a token in localStorage would let a revoked or expired
 *  session render the whole shell and then fail every request inside it, which
 *  looks like the app is broken rather than like the user is signed out. */
function Gate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<"checking" | "in" | "out">(
    getToken() ? "checking" : "out"
  );

  useEffect(() => {
    if (state !== "checking") return;
    api.auth
      .me()
      .then(() => setState("in"))
      .catch(() => setState("out"));
  }, [state]);

  if (state === "checking") return null;
  if (state === "out") return <Navigate to="/signin" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />
      <Route
        path="*"
        element={
          <Gate>
            <Shell>
              <Inner />
            </Shell>
          </Gate>
        }
      />
    </Routes>
  );
}

function Inner() {
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/regions" element={<RegionList />} />
      <Route path="/regions/:id" element={<RegionDetail />} />
      <Route path="/producers" element={<ProducerList />} />
      <Route path="/producers/:id" element={<ProducerDetail />} />
      <Route path="/bottles" element={<BottleList />} />
      <Route path="/bottles/:id" element={<BottleDetail />} />
      <Route path="/tasting-notes" element={<TastingNoteList />} />
      <Route path="/tasting-notes/:id" element={<TastingNoteDetail />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
